import {isAfter} from 'date-fns'
import {dequal} from 'dequal'
import ContentLoader from 'react-content-loader'

import MessageSvg from '@/assets/icons/message.svg?react'
import generateHistoryBreakpoints from '@/lib/chat/generateHistoryBreakpoints'
import useThreadsSuspense from '@/lib/chat/hooks/useChatThreads'
// import TrashSvg from '@/assets/icons/trash.svg?react'
import type {Thread} from '@/models/threadModel'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './ChatThreads.module.scss'

function threadsChunkify(threads: Thread[]) {
  if (threads.length === 0) return []

  threads.sort((a, b) => Number(a) - Number(b))

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- guaranteed existence
  const latestThread = threads[threads.length - 1]!

  const breakpoints = generateHistoryBreakpoints(latestThread.lastUpdatedTime)

  const result = breakpoints.map(breakpoint => {
    const breakpointIndex = threads.findLastIndex(thread =>
      isAfter(thread.lastUpdatedTime, breakpoint.time),
    )
    let breakpointThreads: Thread[] = []

    if (breakpointIndex !== -1) {
      breakpointThreads = threads.slice(0, breakpointIndex + 1)
      threads = threads.slice(breakpointIndex + 1)
    }

    return {
      ...breakpoint,
      threads: breakpointThreads,
    }
  })

  return result.filter(r => r.threads.length > 0)
}

interface Props {
  onSelectThread: (threadId: Thread['id']) => void
  activeThreadId?: Thread['id']
}

interface ChatThreadProps {
  thread: Thread
  isActive: boolean
  onSelectThread: Props['onSelectThread']
}

const ChatThread = memo<ChatThreadProps>(
  function ChatThread({thread, isActive, onSelectThread}) {
    const [isPending, startTransition] = useTransition()

    return (
      <button
        className={clsx(style.ChatThreadItem, (isActive || isPending) && style.Active)}
        onClick={() => {
          startTransition(() => {
            onSelectThread(thread.id)
          })
        }}
        aria-label={`Conversation about ${thread.title}`}
      >
        <div className={style.MessageIcon} aria-hidden>
          <MessageSvg aria-hidden />
        </div>
        <div className={style.ThreadTitle} aria-hidden>
          {thread.title}
        </div>
        {/* <button
          className={style.DeleteThread}
          onClick={e => {
            e.stopPropagation()
          }}
          aria-label='Delete conversation'
        >
          <TrashSvg aria-hidden/>
        </button> */}
      </button>
    )
  },
  (prev, next) => {
    return (
      prev.isActive === next.isActive &&
      prev.onSelectThread === next.onSelectThread &&
      dequal(prev.thread, next.thread)
    )
  },
)

export const SuspensedChatThreads = memo<Props>(function ChatThreads(props) {
  const {data: threads} = useThreadsSuspense({refetchInterval: 10000})

  const chunkifiedThreads = threadsChunkify(threads)

  return (
    <>
      {/* <div className={style.ChatThreadsTop}>
        <h3 className={style.ChatThreadsHeading}>Your conversations</h3>
        <button className={style.ClearAllBtn}>Clear All</button>
      </div> */}
      <div className={style.ChatThreads}>
        <VisuallyHidden>Your conversations</VisuallyHidden>
        {chunkifiedThreads.map(chunk => (
          <div key={chunk.text}>
            <VisuallyHidden>{`Threads from ${chunk.text}`}</VisuallyHidden>
            <span className={style.ChatThreadSection} aria-hidden>
              {chunk.text}
            </span>
            <div className={style.ChatThreadItems}>
              {chunk.threads.map(thread => (
                <ChatThread
                  key={thread.id}
                  thread={thread}
                  isActive={props.activeThreadId === thread.id}
                  onSelectThread={props.onSelectThread}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  )
})

function ChatThreadsIsLoading() {
  return (
    <>
      <ContentLoader
        speed={2}
        width={'100%'}
        height={50}
        viewBox='0 0 100% 50'
        backgroundColor='#151515'
        foregroundColor='#393939'
      >
        <rect x='0' y='10' rx='4' ry='4' width='30' height='30' />
        <rect x='42' y='16' rx='4' ry='4' width='calc(100% - 42px)' height='18' />
      </ContentLoader>
      <ContentLoader
        speed={2}
        width={'100%'}
        height={50}
        viewBox='0 0 100% 50'
        backgroundColor='#151515'
        foregroundColor='#393939'
      >
        <rect x='0' y='10' rx='4' ry='4' width='30' height='30' />
        <rect x='42' y='16' rx='4' ry='4' width='calc(100% - 42px)' height='18' />
      </ContentLoader>
      <ContentLoader
        speed={2}
        width={'100%'}
        height={50}
        viewBox='0 0 100% 50'
        backgroundColor='#151515'
        foregroundColor='#393939'
      >
        <rect x='0' y='10' rx='4' ry='4' width='30' height='30' />
        <rect x='42' y='16' rx='4' ry='4' width='calc(100% - 42px)' height='18' />
      </ContentLoader>
      <ContentLoader
        speed={2}
        width={'100%'}
        height={50}
        viewBox='0 0 100% 50'
        backgroundColor='#151515'
        foregroundColor='#393939'
      >
        <rect x='0' y='10' rx='4' ry='4' width='30' height='30' />
        <rect x='42' y='16' rx='4' ry='4' width='calc(100% - 42px)' height='18' />
      </ContentLoader>
    </>
  )
}

export default memo<Props>(function ChatThreads(props) {
  return (
    <Suspense fallback={<ChatThreadsIsLoading />}>
      <SuspensedChatThreads {...props} />
    </Suspense>
  )
})
